import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import KeyboardAwareView from '../../src/components/KeyboardAwareView';
import { withStepUp, StepUpError } from '../../src/auth/stepUp';
import type { StepUpReturnRoute } from '../../src/auth/stepUp';
import { useAuthStore } from '../../src/stores/authStore';
import { useEncryptionStore } from '../../src/stores/encryptionStore';
import { AxiosError } from 'axios';
import { encryptionApi } from '../../src/api/client';
import { getErrorMessage } from '../../src/utils/errors';
import { clearStoredKey } from '../../src/utils/encryption';
import { useTheme } from '../../src/theme/ThemeContext';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';

const REQUIRED_PHRASE = 'DELETE MY DATA';

/**
 * Where a web step-up sends the browser back to.
 *
 * Setup routes are exempt from the root layout's redirect (app/_layout.tsx), so
 * landing here sticks. The typed phrase is local state and does not survive the
 * navigation — the user retypes it, which is a re-confirmation rather than a
 * cost. It must never be persisted: that would be storing an instruction to
 * destroy data across a page load.
 */
const RETURN_TO: StepUpReturnRoute = '/(setup)/reset-confirm';

export default function ResetConfirmScreen() {
  const router = useRouter();
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const isMatch = confirmation.trim() === REQUIRED_PHRASE;

  /**
   * The reset itself. Bulk-deletes content and cannot be undone — the server
   * holds no key material to restore from.
   */
  const performReset = async () => {
    await encryptionApi.resetEncryption({ confirmation: REQUIRED_PHRASE });

    // Update cached user to reflect server-side e2ee_setup_complete=false
    // (prevents stale flag from short-circuiting the routing guard)
    const authState = useAuthStore.getState();
    if (authState.user) {
      await authState.setUser({ ...authState.user, e2ee_setup_complete: false });
    }

    // Clear local encryption state. `forgetDevice` removes both the device key
    // and the seed; the id is passed because `currentUserId` may already be
    // null here and the store must not fall back to an unscoped slot.
    const userId = authState.user?.id;
    if (userId) await clearStoredKey(userId);
    await useEncryptionStore.getState().forgetDevice(userId);
    useEncryptionStore.getState().setIsNewUser(true);

    router.replace('/(setup)/reset-success');
  };

  const handleReset = async () => {
    if (!isMatch) return;
    setError(null);
    setIsSubmitting(true);

    try {
      // Action first, step up only on the server's challenge — see withStepUp.
      // Calling stepUp() ahead of the reset is what made this screen a no-op on
      // web, and looped the user back to /restore having destroyed nothing.
      await withStepUp(performReset, RETURN_TO);
    } catch (err: unknown) {
      // The browser is already leaving for the hosted login. An error box would
      // flash and vanish, and there is nothing here for the user to act on.
      if (err instanceof StepUpError && Platform.OS === 'web') return;

      const message = err instanceof Error ? err.message : String(err);
      const status = err instanceof AxiosError ? err.response?.status : undefined;

      if (
        err instanceof StepUpError ||
        message.includes('cancelled') ||
        message.includes('canceled')
      ) {
        setError('Passkey verification was cancelled. Please try again to confirm your identity.');
      } else if (status === 429) {
        setError('Too many reset attempts. Please try again later.');
      } else if (status === 401) {
        setError('Identity verification failed. Please try again.');
      } else if (status === 400) {
        setError(getErrorMessage(err, 'Invalid request. Please try again.'));
      } else {
        console.error('Encryption reset failed:', err);
        setError('Failed to reset account. Please check your connection and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KeyboardAwareView style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.card}>
          <View style={styles.iconContainer}>
            <Text style={styles.iconText}>{'\u{1F6A8}'}</Text>
          </View>
          <Text style={styles.title}>Confirm Account Reset</Text>
          <Text style={styles.subtitle}>
            This action is permanent and cannot be undone. All your visions,
            goals, tasks, and scores will be permanently deleted.
          </Text>

          <View style={styles.inputSection}>
            <Text style={styles.inputLabel}>
              Type <Text style={styles.requiredPhrase}>DELETE MY DATA</Text> to
              confirm:
            </Text>
            <TextInput
              style={styles.input}
              value={confirmation}
              onChangeText={setConfirmation}
              placeholder="Type here..."
              placeholderTextColor={colors.textDisabled}
              autoCapitalize="characters"
              autoCorrect={false}
              autoComplete="off"
              editable={!isSubmitting}
              accessibilityLabel="Type DELETE MY DATA to confirm"
              testID="reset-confirm.input"
            />
          </View>

          {error && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          <TouchableOpacity
            style={[
              styles.resetButton,
              (!isMatch || isSubmitting) && styles.buttonDisabled,
            ]}
            onPress={handleReset}
            disabled={!isMatch || isSubmitting}
            activeOpacity={0.7}
            accessibilityLabel="Reset my account"
            accessibilityHint="Permanently deletes all encrypted data"
            accessibilityRole="button"
            accessibilityState={{ disabled: !isMatch || isSubmitting }}
            testID="reset-confirm.reset-button"
          >
            {isSubmitting ? (
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" color={colors.textOnPrimary} />
                <Text style={styles.resetButtonText}> Resetting...</Text>
              </View>
            ) : (
              <Text style={styles.resetButtonText}>Reset My Account</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.cancelButton}
            onPress={() => router.back()}
            activeOpacity={0.7}
            disabled={isSubmitting}
            accessibilityLabel="Cancel"
            accessibilityRole="button"
            testID="reset-confirm.cancel-button"
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAwareView>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1 as const, backgroundColor: theme.colors.brandedBackground },
  scrollContent: {
    flexGrow: 1 as const,
    justifyContent: 'center' as const,
    paddingHorizontal: 16,
    paddingVertical: 32,
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.errorBg,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    marginBottom: 16,
  },
  iconText: { fontSize: 28 },
  title: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    textAlign: 'center' as const,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
    marginBottom: 20,
    lineHeight: 20,
  },
  inputSection: { marginBottom: 16 },
  inputLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: 8,
    lineHeight: 20,
  },
  requiredPhrase: {
    fontWeight: '700' as const,
    color: theme.colors.error,
  },
  input: {
    borderWidth: 2,
    borderColor: theme.colors.border,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: theme.colors.textPrimary,
    fontWeight: '600' as const,
    letterSpacing: 1,
    textAlign: 'center' as const,
  },
  errorBox: {
    backgroundColor: theme.colors.errorBg,
    borderWidth: 1,
    borderColor: theme.colors.errorBorder,
    borderRadius: 10,
    padding: 14,
    marginBottom: 12,
  },
  errorText: { color: theme.colors.errorText, fontSize: 14, lineHeight: 20 },
  resetButton: {
    backgroundColor: theme.colors.error,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 48,
    marginBottom: 10,
  },
  buttonDisabled: { backgroundColor: theme.colors.borderMedium },
  loadingRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  resetButtonText: { color: theme.colors.textOnPrimary, fontSize: 16, fontWeight: '700' as const },
  cancelButton: {
    borderWidth: 1,
    borderColor: theme.colors.borderMedium,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 48,
  },
  cancelButtonText: { color: theme.colors.textMuted, fontSize: 16, fontWeight: '600' as const },
});
